/* Playo — live preview (the channel's public master.m3u8 in hls.js, or
   native HLS on Safari) + a "Now / Next" panel. */

import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import um from "../userManagement/userManagement.module.css";
import s from "./Playo.module.css";
import { getPlaybackUrl, getSchedule, getChannelReady, getReadyQueue, testSource, apiErrorMessage } from "./playoService";
import { DAY, formatTime, normalizeItems, pickNowNext, zonedParts } from "./playoTime";
import { ChannelReadyStatus, ReadyPill } from "./PlayoShared";

const REFRESH_MS = 30 * 1000;
// A signed playback URL is fetched again this long before it expires …
const TOKEN_RENEW_BEFORE_MS = 5 * 60 * 1000;
// … or when the player fails, at most this often.
const TOKEN_RETRY_MS = 60 * 1000;

/** Live channel-ready state (queued / transcoding N% / failed) of an item whose video isn't ready yet. */
function ItemReady({ item }) {
  if (item.channel_ready !== false || !item.asset_id) return null;
  return (
    <div className={s.nnReady} data-testid="item-ready">
      <ChannelReadyStatus videoId={item.asset_id} canEdit={false} />
    </div>
  );
}

/** An item's title; a button that previews its video when there is one. */
function ItemTitle({ item, testId, onPreview }) {
  const title = item.title || "Untitled";
  if (!onPreview || !item.asset_id) {
    return (
      <div className={s.nnTitle} data-testid={testId}>
        {title}
      </div>
    );
  }
  return (
    <button type="button" className={`${s.nnTitle} ${s.nnTitleBtn}`} data-testid={testId} onClick={() => onPreview(item)} title="Preview this video">
      {title} <span className={s.nnPlay}>▶ Preview</span>
    </button>
  );
}

const QUEUE_BUSY_MS = 5000; // while something is queued or running
const QUEUE_IDLE_MS = 30000;
const QUEUE_SHOWN = 6;

/**
 * The tenant's channel-ready transcodes (all channels): the running one with
 * its progress, then the waiting ones with their place in the queue. Hidden
 * when nothing is in flight.
 */
export function TranscodeQueue({ tz = "UTC" }) {
  const [items, setItems] = useState([]);
  const busy = items.length > 0;
  useEffect(() => {
    let alive = true;
    const load = () =>
      Promise.resolve()
        .then(getReadyQueue)
        .then((d) => alive && setItems(Array.isArray(d?.items) ? d.items : []))
        .catch(() => {});
    load();
    const id = setInterval(load, busy ? QUEUE_BUSY_MS : QUEUE_IDLE_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [busy]);
  if (!busy) return null;
  return (
    <div className={`${s.nnBlock} ${s.qBlock}`} data-testid="transcode-queue">
      <div className={s.nnLabel}>Transcode queue · {items.length}</div>
      <div className={s.qList}>
        {items.slice(0, QUEUE_SHOWN).map((q) => (
          <div key={q.video_id} className={s.qRow}>
            <div className={s.qTitle}>
              {q.title || `Video #${q.video_id}`}
              {q.rerun ? <span className={s.muted}> · re-run</span> : null}
            </div>
            <div className={`${s.readyRow} ${s.qMeta}`}>
            {q.state === "processing" ? (
              <>
                <ReadyPill state={{ channel_ready: false, status: "processing", progress: q.progress }} />
                {Number.isFinite(q.progress?.percent) && (
                  <span className={s.readyBar} aria-hidden="true">
                    <span className={s.readyBarFill} style={{ width: `${q.progress.percent}%` }} />
                  </span>
                )}
              </>
            ) : (
              <span className={`${s.pill} ${s.pill_muted}`}>{q.position === 1 ? "Next up" : `#${q.position} in queue`}</span>
            )}
            {q.next_use && (
              <span className={`${s.muted} ${s.qWhere}`}>
                {q.next_use.channel_name} · {formatTime(new Date(q.next_use.start_time).getTime(), tz)}
              </span>
            )}
          </div>
        </div>
        ))}
      </div>
      {items.length > QUEUE_SHOWN && <div className={s.muted}>+{items.length - QUEUE_SHOWN} more</div>}
    </div>
  );
}

const UP_NEXT_MAX = 8;

/** "08:55–09:20", with the day in front when it isn't today (channel timezone). */
function slotLabel(item, now, tz) {
  const time = `${formatTime(item.start, tz)}–${formatTime(item.end, tz)}`;
  if (zonedParts(item.start, tz).date === zonedParts(now, tz).date) return time;
  const day = new Date(item.start).toLocaleDateString("en-GB", { timeZone: tz, weekday: "short", day: "numeric", month: "short" });
  return `${day} · ${time}`;
}

export function NowNext({ channel, items, now = Date.now(), onPreview = null }) {
  const guideOnly = channel.source_type === "external";
  const { now: cur } = pickNowNext(items, now);
  const upcoming = normalizeItems(items).filter((it) => it.start > now);
  const tz = channel.timezone || "UTC";
  const progress = cur ? Math.min(100, Math.max(0, ((now - cur.start) / (cur.end - cur.start)) * 100)) : 0;
  return (
    <div className={s.nowNext} aria-label="Now and next">
      <div className={s.nnBlock}>
        <div className={s.nnLabel}>Now</div>
        {cur ? (
          <>
            <ItemTitle item={cur} testId="now-title" onPreview={onPreview} />
            <div className={s.muted}>
              {formatTime(cur.start, tz)} – {formatTime(cur.end, tz)}
            </div>
            <ItemReady item={cur} />
            <div className={s.progress}>
              <div className={s.progressFill} style={{ width: `${progress}%` }} />
            </div>
          </>
        ) : (
          <div className={s.muted} data-testid="now-title">
            {guideOnly ? "Live stream (no guide entry now)" : channel.filler_asset_id ? "Gap: filler is playing" : "Gap: off air (no filler set)"}
          </div>
        )}
      </div>
      <div className={s.nnBlock}>
        <div className={s.nnLabel}>Up next{upcoming.length > 1 ? ` · ${upcoming.length}` : ""}</div>
        {upcoming.length ? (
          <div className={s.qList}>
            {upcoming.slice(0, UP_NEXT_MAX).map((it, i) => (
              <div key={`${it.id}-${it.start}`} className={s.qRow} data-testid="up-next-row">
                <ItemTitle item={it} testId={i === 0 ? "next-title" : undefined} onPreview={onPreview} />
                <div className={s.muted}>{slotLabel(it, now, tz)}</div>
                <ItemReady item={it} />
              </div>
            ))}
            {upcoming.length > UP_NEXT_MAX && <div className={s.muted}>+{upcoming.length - UP_NEXT_MAX} more in the next 7 days</div>}
          </div>
        ) : (
          <div className={s.muted} data-testid="next-title">
            Nothing scheduled in the next 7 days
          </div>
        )}
      </div>
    </div>
  );
}

/** External channel: where the picture comes from, its last health check, "Check now". */
function SourceCheck({ channel }) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const status = result ? result.status : channel.source_status;
  const error = result ? result.error : channel.source_error;
  const check = async () => {
    setBusy(true);
    try {
      setResult(await testSource(channel.source_url, channel.id));
    } catch (e) {
      setResult({ ok: false, status: "offline", error: apiErrorMessage(e, "Could not check the stream") });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={s.nnBlock} data-testid="source-check">
      <div className={s.nnLabel}>Source stream</div>
      <div className={s.urlRow}>
        <code className={s.url} title={channel.source_url}>
          {channel.source_url}
        </code>
        <button type="button" className={um.btnSubtle} onClick={check} disabled={busy}>
          {busy ? "Checking…" : "Check now"}
        </button>
      </div>
      <div className={status === "online" ? s.okText : status === "offline" ? s.warnText : s.muted}>
        {status === "online" ? "Online" : status === "offline" ? `Offline${error ? `: ${error}` : ""}` : "Not checked yet"}
      </div>
    </div>
  );
}

function Player({ url, onFatal, vod = false }) {
  const ref = useRef(null);
  const [error, setError] = useState("");
  const fatal = useRef(onFatal);
  fatal.current = onFatal;
  useEffect(() => {
    const video = ref.current;
    if (!video || !url) return undefined;
    setError("");
    let hls = null;
    if (Hls.isSupported()) {
      hls = new Hls(vod ? {} : { liveSyncDurationCount: 3 });
      hls.on(Hls.Events.ERROR, (_e, data) => {
        // The token may have run out: a fresh URL is on its way.
        if (data?.fatal && !fatal.current?.()) {
          setError(vod ? "This video couldn't be loaded." : "The live stream couldn't be loaded (off air, the source is down, or no channel-ready content yet).");
        }
      });
      hls.loadSource(url);
      hls.attachMedia(video);
    } else if (video.canPlayType && video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = url;
    } else {
      setError("This browser can't play HLS.");
    }
    return () => {
      if (hls) hls.destroy();
    };
  }, [url, vod]);
  return (
    <>
      <video ref={ref} className={s.video} controls muted autoPlay playsInline data-testid="preview-video" />
      {error && <div className={s.warnText}>{error}</div>}
    </>
  );
}

export default function PlayoPreview({ channel }) {
  const [items, setItems] = useState([]);
  const [tick, setTick] = useState(Date.now());
  const [copied, setCopied] = useState(false);
  const [playback, setPlayback] = useState(null);
  const [playbackError, setPlaybackError] = useState("");
  const [renewal, setRenewal] = useState(0);
  const lastFetch = useRef(0);
  const url = playback?.url || "";
  // On-demand preview of one scheduled video (its channel-ready output) in place of the live stream.
  const [vod, setVod] = useState(null); // { title, url }
  const [vodError, setVodError] = useState("");

  const previewItem = async (item) => {
    setVodError("");
    try {
      const st = await getChannelReady(item.asset_id);
      if (st?.master_url) setVod({ title: item.title || "Untitled", url: st.master_url });
      else setVodError(`“${item.title || "This video"}” isn't channel-ready yet, so there's nothing to preview.`);
    } catch (e) {
      setVodError(apiErrorMessage(e, "Could not load the video"));
    }
  };

  // The tenant may require login, in which case the public playlists need a
  // signed token: ask the backend for the URL instead of building it here.
  useEffect(() => {
    let alive = true;
    if (!renewal) setPlayback(null); // a renewal keeps playing until the new URL is in
    setPlaybackError("");
    lastFetch.current = Date.now();
    getPlaybackUrl(channel.id)
      .then((p) => alive && setPlayback(p))
      .catch((e) => alive && setPlaybackError(apiErrorMessage(e, "Could not get the stream URL")));
    return () => {
      alive = false;
    };
  }, [channel.id, channel.slug, channel.status, renewal]);

  // A signed URL expires (PLAYBACK_TOKEN_TTL_SEC): fetch a new one before it does.
  const expiresAt = playback?.login_required && playback.expires_at ? new Date(playback.expires_at).getTime() : null;
  useEffect(() => {
    if (!expiresAt) return undefined;
    const id = setTimeout(() => setRenewal((n) => n + 1), Math.max(TOKEN_RETRY_MS, expiresAt - TOKEN_RENEW_BEFORE_MS - Date.now()));
    return () => clearTimeout(id);
  }, [expiresAt]);

  // The player failed: with a signed URL that may be an expired token (e.g. the
  // laptop slept through the renewal), so fetch a new one — once a minute at most.
  const renewOnFatal = () => {
    if (!playback?.login_required || Date.now() - lastFetch.current < TOKEN_RETRY_MS) return false;
    setRenewal((n) => n + 1);
    return true;
  };

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const t = Date.now();
      try {
        const list = await getSchedule(channel.id, t - DAY, t + 7 * DAY);
        if (alive) setItems(list);
      } catch {
        /* keep the last answer */
      }
      if (alive) setTick(Date.now());
    };
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [channel.id]);

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };

  return (
    <section className={s.previewCard} aria-label="Preview">
      <div className={s.sectionHead}>Preview</div>
      {vod && (
        <div className={s.vodBar} data-testid="vod-bar">
          <span>
            Previewing <strong>{vod.title}</strong> (on demand, not live)
          </span>
          <button type="button" className={um.btnSubtle} onClick={() => setVod(null)}>
            Back to live
          </button>
        </div>
      )}
      {vodError && <div className={s.warnText}>{vodError}</div>}
      {vod ? (
        <Player key={vod.url} url={vod.url} onFatal={() => false} vod />
      ) : channel.status === "active" ? (
        <Player url={url} onFatal={renewOnFatal} />
      ) : (
        <div className={s.previewOff}>Draft channels aren't on air. Set the status to Active to preview the live stream.</div>
      )}
      <div className={s.urlRow}>
        <code className={s.url} title={url}>
          {url}
        </code>
        <button type="button" className={um.btnSubtle} onClick={copy} disabled={!url}>
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      {playbackError && <div className={s.warnText}>{playbackError}</div>}
      {playback?.login_required && (
        <div className={s.muted} data-testid="token-note">
          This tenant requires sign-in, so this URL carries a playback token that expires{" "}
          {playback.expires_at ? new Date(playback.expires_at).toLocaleString() : "soon"}. Don't hand it to partners as a permanent feed.
        </div>
      )}
      {channel.source_type === "external" && <SourceCheck channel={channel} />}
      <NowNext channel={channel} items={items} now={tick} onPreview={channel.source_type === "external" ? null : previewItem} />
      {channel.source_type !== "external" && <TranscodeQueue tz={channel.timezone || "UTC"} />}
    </section>
  );
}
